import React, { useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from "react";

import {
    OnOpenChangeData,
    OpenPopoverEvents,
    mergeClasses,
    Popover,
    PopoverSurface,
    PositioningImperativeRef,
    PositioningProps,
    resolvePositioningShorthand,
    useFocusFinders
} from "@fluentui/react-components";
import { observer, useLocalObservable } from "mobx-react";
import { useInRouterContext } from "react-router";

import { RenderingStatus } from "Enumerations/RenderingStatus";
import { useSmallScreen } from "Shared/Hooks/Core/useSmallScreen";

import { useStyles, useStylesPopover } from "./M365PulseSearchBox.styles";
import { M365PulseSearchBoxProps } from "./M365PulseSearchBox.types";
import { M365PulseSearchBoxCategoryPopover } from "./M365PulseSearchBoxCategoryPopover";
import { M365PulseSearchBoxFilter } from "./M365PulseSearchBoxFilter";
import { M365PulseSearchboxInput, CLEAR_BUTTON_WIDTH } from "./M365PulseSearchBoxInput";
import { M365PulseSearchBoxPopover } from "./M365PulseSearchBoxPopover";
import { SearchContext, SearchStore } from "./SearchStore";

const M365PulseSearchBoxBase = React.forwardRef((props: M365PulseSearchBoxProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    const {
        disabled,
        "aria-label": ariaLabel,
        value,
        defaultValue,
        loadingStatus = RenderingStatus.Ready,
        searchResultList,
        onSearchChange,
        onValueChange,
        placeholder = "Search",
        className,
        resultType,
        inputAppearance,
        menuPositioning = "below",
        onOpenResultsChange,
        searchingStatus,
        customPopoverContent,
        clearable = false,
        customOnKeyDown,
        disableAutoOpen = false
    } = props;
    const inRouter = useInRouterContext();
    const classnames = useStyles();
    const inputClass = mergeClasses(classnames.input, className?.input);
    const menuitemClass = mergeClasses(classnames.menuitem, className?.menuitem);
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(ref, () => inputRef.current);
    const positioningRef = useRef<PositioningImperativeRef>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const isSmallScreen = useSmallScreen();
    const searchResultsId = useId();
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [width, setWidth] = useState(0);

    const store = useLocalObservable(SearchStore(value ?? defaultValue ?? ""));

    const windowHeight = useRef(window.innerHeight);
    const popoverClass = useStylesPopover(windowHeight.current, isSmallScreen);

    const onOpenChange = useCallback(
        (e: OpenPopoverEvents, data: OnOpenChangeData) => {
            if (onOpenResultsChange) {
                onOpenResultsChange(e, data);
            }

            const selection = window.getSelection();
            const hasSelection = selection && selection.toString().length > 0;
            if (hasSelection || e.target === inputRef.current) {
                store.setOpen(true);
                return;
            }

            store.setOpen(data.open && searchResultList.length > 0);
        },
        [onOpenResultsChange, searchResultList.length, store]
    );

    const onInputFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            const fromResults = e.relatedTarget instanceof Node && popoverRef.current?.contains(e.relatedTarget);
            if (searchResultList.length === 0 || fromResults) {
                store.setOpen(false);
                return;
            }
            store.setOpen(true);
        },
        [searchResultList.length, store]
    );

    const { findFirstFocusable } = useFocusFinders();

    const onTabToResults = useCallback(() => {
        if (popoverRef.current) {
            findFirstFocusable(popoverRef.current)?.focus();
        }
    }, [findFirstFocusable]);

    useEffect(() => {
        positioningRef.current?.setTarget(inputRef.current);
        setWidth(inputRef.current?.parentElement?.offsetWidth || 0);
        store.setInputRef(inputRef.current);
    }, [inputRef.current?.parentElement?.offsetWidth, value, store.keyword, store]);

    useEffect(() => {
        store.searchResultList = searchResultList;
        setActiveIndex(searchResultList.length > 0 ? 0 : -1);
        if (disabled) {
            store.setOpen(false);
        } else if (!disableAutoOpen) {
            store.setOpen(searchResultList.length > 0 && loadingStatus === RenderingStatus.Ready);
        }
    }, [disabled, disableAutoOpen, loadingStatus, searchResultList, store]);

    const styles = useMemo(() => {
        if (isSmallScreen) {
            return {
                width: "100%",
                maxWidth: "100%"
            };
        }

        return {
            width: `${width}px`,
            maxWidth: `${width}px`,
            minWidth: `${width}px`
        };
    }, [width, isSmallScreen]);

    const positioning: PositioningProps = useMemo(() => {
        const hasSearchText = (value !== undefined ? value : store.keyword || "").length > 0;
        const rightOffset = hasSearchText && clearable ? CLEAR_BUTTON_WIDTH / 3 : 0;

        const positioningProps = resolvePositioningShorthand(menuPositioning);
        return {
            ...positioningProps,
            positioningRef,
            offset: { crossAxis: rightOffset, mainAxis: 0 }
        };
    }, [value, store.keyword, clearable, menuPositioning]);

    const hasSearchInput = store.keyword.length > 0;
    const resultCount = searchResultList.length;
    const announcementText = useMemo(() => {
        if (loadingStatus === RenderingStatus.Waiting) {
            return "Searching...";
        }
        if (resultCount === 0 && hasSearchInput) {
            return "No results found.";
        }
        if (resultCount > 0 && store.open) {
            return `${resultCount} result${resultCount === 1 ? "" : "s"} found`;
        }
        return "";
    }, [store.open, loadingStatus, resultCount, hasSearchInput]);

    const activeDescendantId = useMemo(
        () => (store.open && activeIndex >= 0 ? `${searchResultsId}-opt-${activeIndex}` : undefined),
        [store.open, activeIndex, searchResultsId]
    );

    const onSelectIndex = useCallback(
        (index: number) => {
            store.getEnhancedSearchResults()[index]?.onClick();
        },
        [store]
    );

    return (
        <SearchContext.Provider value={store}>
            <div role="status" aria-live="polite" aria-atomic="true" className={classnames.visuallyHidden}>
                {announcementText}
            </div>
            <Popover positioning={positioning} open={store.open} onOpenChange={onOpenChange} unstable_disableAutoFocus>
                <M365PulseSearchboxInput
                    disabled={disabled}
                    aria-label={ariaLabel}
                    className={inputClass}
                    placeholder={placeholder}
                    onSearchChange={onSearchChange}
                    onValueChange={onValueChange}
                    value={value}
                    ref={inputRef}
                    appearance={inputAppearance}
                    searchingStatus={searchingStatus}
                    onFocus={onInputFocus}
                    clearable={clearable}
                    customOnKeyDown={customOnKeyDown}
                    aria-controls={searchResultsId}
                    aria-expanded={store.open}
                    aria-haspopup="listbox"
                    aria-activedescendant={activeDescendantId}
                    onTabToResults={onTabToResults}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                    resultCount={searchResultList.length}
                    onSelectIndex={onSelectIndex}
                />
                <PopoverSurface ref={popoverRef} className={popoverClass} style={styles} id={searchResultsId} role="listbox">
                    {customPopoverContent}
                    {resultType === "category" && inRouter ? (
                        <M365PulseSearchBoxCategoryPopover searchResultsId={searchResultsId} />
                    ) : (
                        <M365PulseSearchBoxPopover className={menuitemClass} activeIndex={activeIndex} searchResultsId={searchResultsId} />
                    )}
                </PopoverSurface>
            </Popover>
        </SearchContext.Provider>
    );
});

const M365PulseSearchBoxWithResults = observer(M365PulseSearchBoxBase);
const M365PulseSearchBoxModeBase = React.forwardRef<HTMLInputElement, M365PulseSearchBoxProps>((props, ref) =>
    props.filterOnly ? <M365PulseSearchBoxFilter {...props} ref={ref} /> : <M365PulseSearchBoxWithResults {...props} ref={ref} />
);

export const M365PulseSearchBox = observer(M365PulseSearchBoxModeBase);
